import React, { useState, useEffect } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  Collapse,
  IconButton,
} from '@mui/material';
import { ExpandMore, ChevronRight, ToggleOn, ToggleOff } from '@mui/icons-material';

export interface TreeNode {
  key: string;
  name: string;
  children?: TreeNode[];
  [key: string]: any; // Allow additional properties
}

interface TreeCheckboxProps<T extends TreeNode> {
  nodes: T[];
  value: T[];
  onChange?: (selectedNodes: T[]) => void;
  readOnly?: boolean;
  title?: string;
  emptyMessage?: string;
  helperText?: string;
  getNodeKey?: (node: T) => string;
  getNodeLabel?: (node: T) => string;
  getNodeChildren?: (node: T) => T[] | undefined;
  renderCustomLabel?: (node: T, level: number) => React.ReactNode;
}

interface TreeNodeComponentProps<T extends TreeNode> {
  node: T;
  selectedNodes: T[];
  onChange?: (selectedNodes: T[]) => void;
  readOnly?: boolean;
  level?: number;
  getNodeKey: (node: T) => string;
  getNodeLabel: (node: T) => string;
  getNodeChildren: (node: T) => T[] | undefined;
  renderCustomLabel?: (node: T, level: number) => React.ReactNode;
}

const TreeNodeComponent = <T extends TreeNode>({
  node,
  selectedNodes,
  onChange,
  readOnly = false,
  level = 0,
  getNodeKey,
  getNodeLabel,
  getNodeChildren,
  renderCustomLabel,
}: TreeNodeComponentProps<T>) => {
  const [expanded, setExpanded] = useState(true);
  
  const nodeKey = getNodeKey(node);
  const nodeLabel = getNodeLabel(node);
  const nodeChildren = getNodeChildren(node);
  
  const isSelected = selectedNodes.some(n => getNodeKey(n) === nodeKey);
  const hasChildren = nodeChildren && nodeChildren.length > 0;

  const handleToggle = () => {
    if (readOnly || !onChange) return;
    
    const newSelectedNodes = [...selectedNodes];
    
    if (isSelected) {
      // Remove only this specific node (independent selection)
      const index = newSelectedNodes.findIndex(n => getNodeKey(n) === nodeKey);
      if (index > -1) {
        newSelectedNodes.splice(index, 1);
      }
    } else {
      // Add only this specific node (independent selection)
      if (!newSelectedNodes.some(n => getNodeKey(n) === nodeKey)) {
        newSelectedNodes.push(node);
      }
    }
    
    onChange(newSelectedNodes);
  };

  const handleChildChange = (childSelectedNodes: T[]) => {
    if (readOnly || !onChange) return;
    onChange(childSelectedNodes);
  };

  const handleToggleAllChildren = () => {
    if (readOnly || !onChange || !hasChildren) return;
    
    const newSelectedNodes = [...selectedNodes];
    
    // Get all descendants (children and grandchildren, etc.)
    const getAllDescendants = (node: T): T[] => {
      const descendants = [node];
      const children = getNodeChildren(node);
      if (children) {
        children.forEach(child => {
          descendants.push(...getAllDescendants(child));
        });
      }
      return descendants;
    };
    
    // Get all descendants (but not the parent)
    const allDescendants = nodeChildren!.flatMap(child => getAllDescendants(child));
    
    // Check if parent is selected AND all descendants are selected
    const parentSelected = selectedNodes.some(n => getNodeKey(n) === nodeKey);
    const allDescendantsSelected = allDescendants.every(descendant => 
      selectedNodes.some(n => getNodeKey(n) === getNodeKey(descendant))
    );
    
    // If parent and all descendants are selected, deselect all (including parent)
    // Otherwise, select all (including parent)
    if (parentSelected && allDescendantsSelected) {
      // Remove parent and all descendants
      const nodesToRemove = [node, ...allDescendants];
      nodesToRemove.forEach(nodeToRemove => {
        const index = newSelectedNodes.findIndex(n => getNodeKey(n) === getNodeKey(nodeToRemove));
        if (index > -1) {
          newSelectedNodes.splice(index, 1);
        }
      });
    } else {
      // Add parent and all descendants that aren't already selected
      const nodesToAdd = [node, ...allDescendants];
      nodesToAdd.forEach(nodeToAdd => {
        if (!newSelectedNodes.some(n => getNodeKey(n) === getNodeKey(nodeToAdd))) {
          newSelectedNodes.push(nodeToAdd);
        }
      });
    }
    
    onChange(newSelectedNodes);
  };

  return (
    <Box sx={{ ml: level * 2 }}>
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center',
          minHeight: 40,
          '&:hover': readOnly ? {} : {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            borderRadius: 1,
          }
        }}
      >
        {hasChildren && (
          <IconButton 
            size="small" 
            onClick={() => setExpanded(!expanded)}
            sx={{ mr: 1 }}
          >
            {expanded ? <ExpandMore /> : <ChevronRight />}
          </IconButton>
        )}
        
        {!hasChildren && <Box sx={{ width: 40 }} />}
        
        <FormControlLabel
          control={
            <Checkbox
              checked={isSelected}
              onChange={handleToggle}
              disabled={readOnly}
              size="small"
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {renderCustomLabel ? (
                renderCustomLabel(node, level)
              ) : (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: level === 0 ? 600 : 400,
                    color: readOnly && !isSelected ? 'text.secondary' : 'text.primary'
                  }}
                >
                  {nodeLabel}
                </Typography>
              )}
              
              {hasChildren && !readOnly && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleToggleAllChildren();
                  }}
                  onMouseDown={(e) => {
                    // Prevent label from toggling the checkbox when pressing the button
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  sx={{ 
                    p: 0.25,
                    ml: 0.5,
                    color: 'text.secondary',
                    '&:hover': {
                      color: 'primary.main',
                    }
                  }}
                  title="Toggle this parent and all children"
                >
                  {(() => {
                    // Check if parent and all descendants are selected to show appropriate icon
                    const getAllDescendants = (node: T): T[] => {
                      const descendants = [node];
                      const children = getNodeChildren(node);
                      if (children) {
                        children.forEach(child => {
                          descendants.push(...getAllDescendants(child));
                        });
                      }
                      return descendants;
                    };
                    
                    // Get all descendants (but not the parent)
                    const allDescendants = nodeChildren!.flatMap(child => getAllDescendants(child));
                    
                    // Check if parent is selected AND all descendants are selected
                    const parentSelected = selectedNodes.some(n => getNodeKey(n) === nodeKey);
                    const allDescendantsSelected = allDescendants.every(descendant => 
                      selectedNodes.some(n => getNodeKey(n) === getNodeKey(descendant))
                    );
                    
                    // Show ToggleOn when everything is selected (can turn off)
                    // Show ToggleOff when not everything is selected (can turn on)
                    return (parentSelected && allDescendantsSelected) ? <ToggleOn fontSize="small" /> : <ToggleOff fontSize="small" />;
                  })()}
                </IconButton>
              )}
            </Box>
          }
          sx={{ 
            flex: 1,
            margin: 0,
            '& .MuiFormControlLabel-label': {
              fontSize: level === 0 ? '0.875rem' : '0.8125rem'
            }
          }}
        />
      </Box>
      
      {hasChildren && (
        <Collapse in={expanded}>
          <Box sx={{ ml: 1 }}>
            {nodeChildren!.map((child) => (
              <TreeNodeComponent
                key={getNodeKey(child)}
                node={child}
                selectedNodes={selectedNodes}
                onChange={handleChildChange}
                readOnly={readOnly}
                level={level + 1}
                getNodeKey={getNodeKey}
                getNodeLabel={getNodeLabel}
                getNodeChildren={getNodeChildren}
                renderCustomLabel={renderCustomLabel}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export const TreeCheckbox = <T extends TreeNode>({
  nodes,
  value,
  onChange,
  readOnly = false,
  title,
  emptyMessage = "No hay elementos disponibles",
  helperText,
  getNodeKey = (node: T) => node.key,
  getNodeLabel = (node: T) => node.name,
  getNodeChildren = (node: T) => node.children as T[] | undefined,
  renderCustomLabel,
}: TreeCheckboxProps<T>) => {
  const [selectedNodes, setSelectedNodes] = useState<T[]>(value || []);

  useEffect(() => {
    setSelectedNodes(value || []);
  }, [value]);

  const handleChange = (newSelectedNodes: T[]) => {
    setSelectedNodes(newSelectedNodes);
    onChange?.(newSelectedNodes);
  };

  if (!nodes || nodes.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {title && (
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
      )}
      <Box sx={{ mt: 2 }}>
        {nodes.map((node) => (
          <TreeNodeComponent
            key={getNodeKey(node)}
            node={node}
            selectedNodes={selectedNodes}
            onChange={handleChange}
            readOnly={readOnly}
            getNodeKey={getNodeKey}
            getNodeLabel={getNodeLabel}
            getNodeChildren={getNodeChildren}
            renderCustomLabel={renderCustomLabel}
          />
        ))}
      </Box>
      {!readOnly && helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};